import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const ProjectCard = ({ p }) => (
  <Link to={`/projects/${p.slug}`} data-testid={`project-card-${p.slug}`} className="group relative shrink-0 w-[80vw] sm:w-[440px] lg:w-[520px] overflow-hidden bg-onyx-black">
    <div className="relative h-[560px] overflow-hidden">
      <img src={fileUrl(p.hero_image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-out" draggable="false" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent" />
      <div className="absolute top-6 left-6 flex items-center gap-3">
        <span className="text-[10px] tracking-[0.2em] uppercase bg-white/10 backdrop-blur-md text-white px-3 py-1.5 border border-white/20">{p.type}</span>
      </div>
      <div className="absolute inset-x-0 bottom-0 p-7">
        <div className="flex items-end justify-between">
          <div>
            <h3 className="text-white font-display font-bold text-3xl tracking-tight">{p.name}</h3>
            <p className="text-white/60 text-sm mt-2">{p.location} · {p.year}</p>
            <p className="text-white/70 text-sm mt-4 max-w-sm max-h-0 group-hover:max-h-24 overflow-hidden opacity-0 group-hover:opacity-100 transition-all duration-500">{p.short_description}</p>
          </div>
          <div className="w-12 h-12 shrink-0 border border-white/30 flex items-center justify-center group-hover:bg-onyx-wood group-hover:border-onyx-wood transition-colors">
            <ArrowUpRight size={18} className="text-white group-hover:text-onyx-black transition-colors" />
          </div>
        </div>
      </div>
    </div>
  </Link>
);

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const trackRef = useRef(null);
  const [constraint, setConstraint] = useState(0);
  const x = useMotionValue(0);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const calc = () => {
      if (trackRef.current) setConstraint(trackRef.current.scrollWidth - trackRef.current.offsetWidth + 96);
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [projects]);

  return (
    <div className="bg-onyx-charcoal min-h-screen text-white">
      <section className="max-w-[1600px] mx-auto px-6 lg:px-12 pt-40 pb-12">
        <span className="text-[11px] tracking-[0.3em] uppercase text-onyx-wood">Projects</span>
        <h1 className="font-display font-extrabold text-5xl md:text-8xl tracking-tightest mt-5 leading-[0.9]">Selected Work</h1>
        <p className="text-white/50 text-lg mt-8 max-w-2xl">Drag to explore. A gallery of envelopes engineered from dirt to door key.</p>
      </section>

      <section className="pb-16 overflow-hidden" data-testid="projects-gallery">
        <motion.div
          ref={trackRef}
          drag="x"
          dragConstraints={{ left: -constraint, right: 0 }}
          dragElastic={0.08}
          style={{ x }}
          className="flex gap-6 px-6 lg:px-12 cursor-grab active:cursor-grabbing"
        >
          {projects.map((p) => <ProjectCard key={p.id} p={p} />)}
        </motion.div>
      </section>

      {/* Fallback stacked grid for context / mobile clarity */}
      <section className="bg-onyx-cream text-onyx-charcoal py-24">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-10 text-onyx-metal">All Projects</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p, i) => (
              <Reveal key={p.id} delay={(i % 3) * 0.08}>
                <Link to={`/projects/${p.slug}`} className="group block">
                  <div className="relative h-72 overflow-hidden">
                    <img src={fileUrl(p.hero_image)} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="pt-4 flex justify-between items-start">
                    <div>
                      <h3 className="font-display font-bold text-lg tracking-tight">{p.name}</h3>
                      <p className="text-onyx-metal text-sm mt-1">{p.location} · {p.type}</p>
                    </div>
                    <span className="font-mono text-sm text-onyx-wood">{p.year}</span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
